import React, { useState, useRef, useEffect } from 'react';
import { 
  HiMenu, 
  HiChevronDown, 
  HiChevronUp, 
  HiPencilAlt, // Pencil icon for new message
} from 'react-icons/hi';
import { Link } from 'react-router-dom';
import { Conversation } from '@/contexts/ChatContext';

interface MobileChatHeaderProps {
  currentConversation?: Conversation | null;
  conversations: Conversation[];
  onSelectConversation: (id: string) => void;
  onNewChat: () => void;
  onMenuClick: () => void;
}

const MobileChatHeader: React.FC<MobileChatHeaderProps> = ({
  currentConversation,
  conversations,
  onSelectConversation,
  onNewChat,
  onMenuClick
}) => {
  const [showConversations, setShowConversations] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Sort conversations by updated date (newest first)
  const sortedConversations = [...conversations].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
  
  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowConversations(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Toggle conversation dropdown
  const toggleConversations = () => {
    setShowConversations(!showConversations);
  };
  
  // Select a conversation and close dropdown
  const handleSelectConversation = (id: string) => {
    onSelectConversation(id);
    setShowConversations(false);
  };
  
  // Start new chat and close dropdown
  const handleNewChat = () => {
    onNewChat();
    setShowConversations(false);
  };
  
  return (
    <header className="fixed top-0 left-0 right-0 z-20 bg-white border-b border-gray-200 shadow-sm">
      <div className="flex items-center justify-between h-14 px-3">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img 
            src="/assets/GetSecondOpinion_483sq.png" 
            alt="Get Second Opinion" 
            className="h-8 w-8"
          />
        </Link>
        
        {/* Conversation Selector - White background to match header */}
        <div className="relative flex-1 max-w-[60%] mx-3" ref={dropdownRef}>
          <button 
            className="flex items-center justify-between w-full px-3 py-1.5 text-left text-gray-800 rounded-lg truncate"
            onClick={toggleConversations}
            aria-expanded={showConversations}
            aria-haspopup="true"
          >
            <span className="truncate font-medium">
              {currentConversation?.title || 'New Conversation'}
            </span>
            <span className="flex items-center justify-center ml-1 w-5 h-5 text-gray-500">
              {showConversations ? 
                <HiChevronUp className="flex-shrink-0 h-5 w-5" /> : 
                <HiChevronDown className="flex-shrink-0 h-5 w-5" />
              }
            </span>
          </button>
          
          {/* Dropdown menu */}
          {showConversations && (
            <div className="absolute top-full left-0 w-64 mt-1 py-1 bg-white border border-gray-200 rounded-lg shadow-lg z-30 max-h-96 overflow-y-auto">
              {/* New Chat button at the top */}
              <button
                className="flex items-center w-full px-4 py-2 text-left text-blue-600 hover:bg-blue-50 border-b border-gray-100"
                onClick={handleNewChat}
              >
                <HiPencilAlt className="mr-2 h-5 w-5" />
                <span>New Chat</span>
              </button>
              
              {/* Conversation list */}
              <div className="max-h-80 overflow-y-auto">
                {sortedConversations.length === 0 ? (
                  <div className="px-4 py-2 text-sm text-gray-500">
                    No conversations yet
                  </div>
                ) : (
                  sortedConversations.map(conversation => (
                    <button
                      key={conversation.id}
                      className={`flex items-center w-full px-4 py-2 text-left hover:bg-gray-100 ${
                        conversation.id === currentConversation?.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                      onClick={() => handleSelectConversation(conversation.id)}
                    >
                      <span className="truncate">{conversation.title}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={onNewChat}
            className="p-2 rounded-full text-gray-700 hover:bg-gray-100"
            aria-label="New chat"
          >
            <HiPencilAlt className="h-5 w-5" /> {/* Changed from HiPlus to HiDocumentAdd */}
          </button>
          
          <button
            onClick={onMenuClick}
            className="p-2 rounded-full text-gray-700 hover:bg-gray-100"
            aria-label="Menu"
          >
            <HiMenu className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default MobileChatHeader;